import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Globe2, CheckCircle2 } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Icon, Eyebrow, FAQ } from "./publicUI";
import {
  COMPANY, IMG, ABOUT_INTRO, VERTICALS, GEN_EMIRA, KEY_DATA, COMPETENCE,
  SERVICES, WHY_CHOOSE, WHY_BADGES, COUNTRIES, flagUrl, FAQS,
} from "./siteContent";

function Stat({ value, label }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; const target = value || 0; const start = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - start) / 1200); setN(Math.floor(p * target)); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick); return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="text-center">
      <div className="font-display text-3xl sm:text-4xl font-extrabold text-white">{n}<span className="text-[#0088FF]">+</span></div>
      <div className="text-xs sm:text-sm text-slate-400 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const [stats, setStats] = useState({ candidates: 0, jobs: 0, countries: 0, deployed: 0 });
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => { client.get("/public/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative brand-gradient-navy overflow-hidden">
        <div className="absolute inset-0 brand-dotgrid opacity-60" />
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#0088FF]/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-sky-200 text-xs font-medium mb-6">
              <Globe2 className="h-4 w-4" /> {COMPANY.name}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              Empowering Global Workforces, <span className="text-[#0088FF]">One Skill at a Time</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              An end-to-end workforce management enterprise — sourcing, training and deploying job-ready talent
              across the Gulf, Europe and beyond, on one connected platform from application to deployment.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button data-testid="hero-find-job-btn" size="lg" onClick={() => nav("/careers")} className="bg-[#0088FF] hover:bg-[#0070E0] h-12 text-base">
                <Search className="h-5 w-5 mr-2" /> Explore Careers
              </Button>
              <Button data-testid="hero-hire-btn" size="lg" variant="outline" onClick={() => nav("/for-employers")} className="h-12 text-base bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">
                Hire Workforce
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-4 gap-4 max-w-lg border-t border-white/10 pt-6">
              <Stat value={stats.countries} label="Countries" />
              <Stat value={stats.jobs} label="Open Jobs" />
              <Stat value={stats.candidates} label="Candidates" />
              <Stat value={stats.deployed} label="Deployed" />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative hidden lg:block">
            <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img src={IMG.hero} alt="Skilled global workforce" className="w-full h-[480px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 w-56">
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold"><CheckCircle2 className="h-4 w-4" /> Deployment Ready</div>
              <p className="text-xs text-slate-500 mt-1">Screening, training, medical, visa &amp; travel — handled on one platform.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="max-w-5xl mx-auto px-6 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 px-3 bg-slate-50 rounded-xl border border-slate-200">
            <Search className="h-5 w-5 text-slate-400" />
            <input data-testid="home-job-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && nav(`/jobs?q=${encodeURIComponent(q)}`)}
              placeholder="Search jobs by position, country or skill..." className="flex-1 bg-transparent py-3 outline-none text-sm" />
          </div>
          <Button data-testid="home-search-btn" onClick={() => nav(`/jobs?q=${encodeURIComponent(q)}`)} className="bg-[#0A192F] hover:bg-[#0B2545] h-12 px-8">Search Jobs</Button>
        </div>
      </section>

      {/* ABOUT + VERTICALS */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <div className="rounded-3xl overflow-hidden shadow-xl"><img src={IMG.aboutMeeting} alt="Emira team" className="w-full h-[440px] object-cover" /></div>
          <div className="absolute -bottom-6 -right-6 bg-[#0088FF] text-white rounded-2xl shadow-xl p-5 w-44 hidden sm:block">
            <div className="font-display text-3xl font-extrabold">2+</div>
            <div className="text-xs text-sky-100">decades of manpower expertise</div>
          </div>
        </div>
        <div>
          <Eyebrow>About Company</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Comprehensive workforce solutions, customized for every client</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">{ABOUT_INTRO}</p>
          <div className="mt-7 grid sm:grid-cols-3 gap-4">
            {VERTICALS.map((v) => (
              <div key={v.code} className="rounded-2xl border border-slate-200 p-5 hover-lift bg-white">
                <div className="inline-flex items-center justify-center h-9 px-3 rounded-lg bg-[#0A192F] text-white font-display font-bold text-sm">{v.code}</div>
                <div className="font-semibold text-[#0A192F] mt-3 text-sm">{v.title}</div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GEN EMIRA + KEY DATA */}
      <section className="brand-gradient-navy relative overflow-hidden">
        <div className="absolute inset-0 brand-dotgrid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <Eyebrow><span className="text-sky-300">GenEmira · The Tech Stack</span></Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mt-2">A digital exchange connecting the world's workforce</h2>
            <p className="text-slate-300 mt-4 leading-relaxed">{GEN_EMIRA}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {KEY_DATA.map((k) => (
              <div key={k.label} className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur">
                <div className="font-display text-3xl font-extrabold text-white">{k.unit}</div>
                <div className="text-sm font-semibold text-sky-200 mt-2">{k.label}</div>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{k.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISTINCTIVE COMPETENCE */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl"><Eyebrow>Distinctive Competence</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Why employers trust Emira for scale</h2></div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {COMPETENCE.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="hover-lift bg-white rounded-2xl border border-slate-200 p-6">
              <div className="h-12 w-12 rounded-xl bg-[#0088FF]/10 grid place-items-center text-[#0088FF] mb-4"><Icon name={c.icon} className="h-6 w-6" /></div>
              <h3 className="font-display font-semibold text-lg text-[#0A192F]">{c.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl"><Eyebrow>Services We Offer</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Reliable manpower solutions to power your business</h2></div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="hover-lift bg-white rounded-2xl border border-slate-200 p-7">
                <div className="h-12 w-12 rounded-xl bg-[#0A192F] grid place-items-center text-white mb-4"><Icon name={s.icon} className="h-6 w-6" /></div>
                <h3 className="font-display font-semibold text-lg text-[#0A192F]">{s.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8"><Button data-testid="home-services-btn" variant="outline" onClick={() => nav("/services")}>View all services <ArrowRight className="h-4 w-4 ml-1" /></Button></div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <div className="rounded-3xl overflow-hidden shadow-xl order-2 lg:order-1"><img src={IMG.scaffolding} alt="Skilled workers on site" className="w-full h-[460px] object-cover" /></div>
        <div className="order-1 lg:order-2">
          <Eyebrow>Why Choose Emira</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Quality, speed and reliability in global workforce solutions</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">{WHY_CHOOSE}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {WHY_BADGES.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0088FF]/10 text-[#0088FF] text-sm font-medium"><CheckCircle2 className="h-4 w-4" /> {b}</span>
            ))}
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500">
            <span className="font-semibold text-[#0A192F]">Awards &amp; Recognition</span> — honoured for service excellence &amp; innovation.
          </div>
        </div>
      </section>

      {/* COUNTRIES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl"><Eyebrow>Global Presence</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Major countries we provide work visas &amp; immigration consulting for</h2></div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5">
            {COUNTRIES.map((c, i) => (
              <motion.div key={c.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className="bg-white rounded-2xl border border-slate-200 p-5 text-center hover-lift">
                <img src={flagUrl(c.iso)} alt={c.name} className="h-10 w-auto mx-auto rounded shadow-sm object-cover" loading="lazy" />
                <div className="font-display font-semibold text-[#0A192F] mt-3 text-sm">{c.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-10"><Eyebrow>Emira Manpower &amp; Training Solutions</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Frequently Asked Questions</h2></div>
        <FAQ items={FAQS} />
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="brand-gradient-navy rounded-3xl p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute inset-0 brand-dotgrid opacity-40" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Ready to build your global workforce?</h2>
              <p className="text-slate-300 mt-3">{COMPANY.positioning}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Button data-testid="cta-quote-btn" size="lg" onClick={() => nav("/for-employers")} className="bg-[#0088FF] hover:bg-[#0070E0] h-12">Request a Quote</Button>
              <Button data-testid="cta-contact-btn" size="lg" variant="outline" onClick={() => nav("/contact")} className="h-12 bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">Contact Emira</Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
